// src/services/attachmentService.js
// Carpetas y archivos del apartado "Attachments" de un reclamo (Claim
// Status / ClaimDetailPage). Los archivos en sí viajan por el mismo backend
// de respaldos que check/payment/closing statement (backupService.js ->
// public/api/backup.php, tipo 'attachment'), no por Firebase Storage — este
// proyecto no lo usa. Aquí solo se guarda la metadata en Firestore.
//
// Las carpetas pueden anidarse (carpeta dentro de carpeta) vía
// `parentFolderId` — null significa "carpeta de primer nivel, dentro del
// reclamo". No hay límite de profundidad; ClaimAttachments.jsx navega con
// una pila de breadcrumbs.
import { auth, db } from '../firebase/config';
import { uploadBackup, openBackup } from './backupService';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';

// Se ordena en el cliente (por createdAt/uploadedAt) en vez de con orderBy()
// en la query: así el where(...) solo necesita índices de campo único, que
// Firestore crea automáticamente, sin agregar índices compuestos nuevos a
// firestore.indexes.json.
const sortByTimestamp = (list, field) =>
  [...list].sort((a, b) => (a[field]?.toMillis?.() || 0) - (b[field]?.toMillis?.() || 0));

const FOLDERS_COLLECTION = 'attachmentFolders';
const FILES_COLLECTION = 'attachmentFiles';

const currentUserInfo = () => {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión.');
  return { email: user.email, name: user.displayName || user.email };
};

// Subcarpetas directas de `parentFolderId` (null = primer nivel, dentro del
// reclamo). Dos filtros de igualdad (claimRecordId + parentFolderId) no
// necesitan índice compuesto en Firestore.
export const getAttachmentFolders = async (claimRecordId, parentFolderId = null) => {
  const snapshot = await getDocs(
    query(
      collection(db, FOLDERS_COLLECTION),
      where('claimRecordId', '==', claimRecordId),
      where('parentFolderId', '==', parentFolderId)
    )
  );
  return sortByTimestamp(snapshot.docs.map(d => ({ id: d.id, ...d.data() })), 'createdAt');
};

// Todas las carpetas del reclamo sin importar el nivel — usado solo para
// resolver nombres (breadcrumbs del buscador global), no para navegar.
export const getAllAttachmentFolders = async (claimRecordId) => {
  const snapshot = await getDocs(
    query(collection(db, FOLDERS_COLLECTION), where('claimRecordId', '==', claimRecordId))
  );
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const createAttachmentFolder = async (claimRecordId, parentFolderId, name) => {
  const trimmed = (name || '').trim();
  if (!trimmed) throw new Error('El nombre de la carpeta es obligatorio.');
  const { email, name: displayName } = currentUserInfo();
  const docRef = await addDoc(collection(db, FOLDERS_COLLECTION), {
    claimRecordId,
    parentFolderId: parentFolderId || null,
    name: trimmed,
    createdByEmail: email,
    createdByName: displayName,
    createdAt: serverTimestamp()
  });
  return docRef.id;
};

export const renameAttachmentFolder = async (folderId, name) => {
  const trimmed = (name || '').trim();
  if (!trimmed) throw new Error('El nombre de la carpeta es obligatorio.');
  await updateDoc(doc(db, FOLDERS_COLLECTION, folderId), { name: trimmed });
};

// Borra la carpeta, sus archivos y recursivamente todas sus subcarpetas
// (con sus propios archivos y subcarpetas, etc).
export const deleteAttachmentFolder = async (folderId) => {
  const [subfolderSnapshot, files] = await Promise.all([
    getDocs(query(collection(db, FOLDERS_COLLECTION), where('parentFolderId', '==', folderId))),
    getAttachmentFiles(folderId)
  ]);
  await Promise.all(subfolderSnapshot.docs.map(d => deleteAttachmentFolder(d.id)));
  await Promise.all(files.map(f => deleteDoc(doc(db, FILES_COLLECTION, f.id))));
  await deleteDoc(doc(db, FOLDERS_COLLECTION, folderId));
};

export const getAttachmentFiles = async (folderId) => {
  const snapshot = await getDocs(
    query(collection(db, FILES_COLLECTION), where('folderId', '==', folderId))
  );
  return sortByTimestamp(snapshot.docs.map(d => ({ id: d.id, ...d.data() })), 'uploadedAt');
};

// Todos los archivos del reclamo, sin importar la carpeta ni el nivel —
// usado por el buscador del explorador (ClaimAttachments.jsx) para buscar
// por nombre a través de todas las carpetas de una sola vez.
export const getAllAttachmentFiles = async (claimRecordId) => {
  const snapshot = await getDocs(
    query(collection(db, FILES_COLLECTION), where('claimRecordId', '==', claimRecordId))
  );
  return sortByTimestamp(snapshot.docs.map(d => ({ id: d.id, ...d.data() })), 'uploadedAt');
};

export const uploadAttachmentFile = async ({ file, claimRecordId, folderId }) => {
  const { email, name: displayName } = currentUserInfo();
  const backup = await uploadBackup({ file, claimId: claimRecordId, invoiceNumber: '', type: 'attachment' });
  const docRef = await addDoc(collection(db, FILES_COLLECTION), {
    claimRecordId,
    folderId,
    storageKey: backup.storageKey,
    name: backup.name,
    mimeType: backup.mimeType,
    size: backup.size,
    uploadedByEmail: email,
    uploadedByName: displayName,
    uploadedAt: serverTimestamp()
  });
  return docRef.id;
};

export const deleteAttachmentFile = async (fileId) => {
  await deleteDoc(doc(db, FILES_COLLECTION, fileId));
};

export const openAttachmentFile = (storageKey) => openBackup(storageKey);
